import { BEGIN, REVERT } from 'redux-optimist'
import { v4 as uuidV4 } from 'uuid'

import graphql from 'api/graphql'
import { searchTickets } from 'api/search'
import { actionsPartials, filterableFieldsPartials } from 'api/tickets'

import * as types from 'constants/action_types'
import * as pages from 'constants/pages'
import { NEWEST } from 'constants/defaults'

import { doClearNonNamedTicketSearchResults } from 'actions/search'

import { mergedTicketPayload } from 'optimistic/merger'

import { oauthTokenSelector, selectCurrentFolderId } from 'selectors/app'
import {
  selectMergeId,
  selectTargetTicketId,
  selectChildTicketIds,
} from 'selectors/merge'

import {
  selectCurrentTicketSearchQueryObject,
  selectMergeableForCurrentCustomer,
  selectTicketSearchOperatorValueMap,
} from 'selectors/search'
import { selectCurrentCustomer } from 'selectors/tickets'
import { selectAccountPreferenceSortByCollaboratorCommentEnabled } from 'selectors/app/selectAccountPreferences'
import { constructSearchQueryId } from 'util/search'

import {
  doUpdateMergeSnippetsVisibility,
  doFetchMergeSnippets,
} from './snippets'

export { doUpdateMergeSnippetsVisibility }

// We pass in the 'current' ticket id so that we can hydrate
// optimisticMergeTicketsById with the ticket already in byId
export function doSwapMergeDirection(currentTicketId) {
  return {
    type: types.SWAP_MERGE_DIRECTION,
    data: { currentTicketId },
  }
}

export function doSelectTicketsToMerge(childTicketIds, mergeId) {
  return {
    type: types.TICKETS_SELECTED_FOR_MERGE,
    data: {
      childTicketIds,
      mergeId,
    },
  }
}

export function doCancel() {
  return {
    type: types.CANCEL_MERGE,
  }
}

export function doFetchMergeableTickets(requestedPage) {
  return (dispatch, getState) => {
    const state = getState()
    const customer = selectCurrentCustomer(state)
    if (!customer) return false
    const customerId = customer.id
    const token = oauthTokenSelector(state)

    // TODO: (convo-reordering-after-full-migrations): remove
    const sortByCollaboratorCommentAtEnabled = selectAccountPreferenceSortByCollaboratorCommentEnabled(
      state
    )
    let page = requestedPage

    if (!page) {
      const currentMergeable = selectMergeableForCurrentCustomer(state) || {
        currentPage: 0,
      }
      if (!currentMergeable.resultsStale) {
        page = currentMergeable.currentPage + 1
      } else {
        page = 1
      }
    }

    const searchQueryObject = {
      customer: customerId,
    }

    dispatch({
      type: types.MERGEABLE_SEARCH_REQUEST,
      data: { page, customerId },
    })

    return searchTickets(
      token,
      searchQueryObject,
      NEWEST,
      page,
      sortByCollaboratorCommentAtEnabled
    ).then(data => {
      const { currentPage, totalPages, totalCount, tickets } = data
      const valueMap = selectTicketSearchOperatorValueMap(state)
      const queryId = constructSearchQueryId(searchQueryObject, valueMap)

      dispatch({
        type: types.MERGEABLE_SEARCH_SUCCESS,
        data: {
          page,
          queryId,
          queryObject: searchQueryObject,
          currentPage,
          totalPages,
          totalCount,
          tickets,
        },
      })

      dispatch(doFetchMergeSnippets(tickets))
    })
  }
}

export function doMerge(mergeId) {
  return (dispatch, getState) => {
    const state = getState()

    // If we canceled the merge in the meantime, dont honour this request.
    if (selectMergeId(state) !== mergeId) return false

    const targetTicketId = selectTargetTicketId(state)
    let childTicketIds = selectChildTicketIds(state)

    if (childTicketIds && !Array.isArray(childTicketIds)) {
      childTicketIds = [childTicketIds]
    }

    const transactionId = uuidV4()
    const optimisticData = mergedTicketPayload(
      state,
      targetTicketId,
      childTicketIds,
      selectCurrentFolderId(state),
      selectCurrentTicketSearchQueryObject(state),
      transactionId
    )

    dispatch({
      type: types.MERGE_TICKETS_REQUEST,
      data: optimisticData,
      optimist: optimisticData ? { type: BEGIN, id: transactionId } : null,
      meta: {
        requestId: transactionId,
      },
    })

    const token = oauthTokenSelector(state)
    const mutation = `
      mutation mergeTickets($ticketId: String, $childTicketIds: [String], $changesetId: String) {
        mergeTickets(ticketId: $ticketId, childTicketIds: $childTicketIds, changesetId: $changesetId) {
          ticket {
            id
            diff
            ${actionsPartials}
            ticket {
              state
              priority
              message_count
              labels {
                id
                name
              }
              ${filterableFieldsPartials}
              system_updated_at
            }
          }
          mergedTickets {
            tickets {
              id
              diff
            }
          }
        }
      }
    `

    const variables = {
      ticketId: targetTicketId,
      childTicketIds,
      changesetId: transactionId,
    }

    dispatch({
      type: pages.TICKET_PAGE,
      payload: { id: targetTicketId },
    })

    return graphql(token, mutation, variables, transactionId)
      .then(res => {
        const data = res.json.data
        const { ticket, mergedTickets } = data.mergeTickets

        if (ticket.diff) {
          ticket.diff = JSON.parse(ticket.diff) // eslint-disable-line no-param-reassign
        }

        if (mergedTickets && mergedTickets.tickets) {
          mergedTickets.tickets.map(t => {
            if (t.diff) {
              t.diff = JSON.parse(t.diff) // eslint-disable-line no-param-reassign
            }
            return t
          })
        }

        dispatch({
          type: types.MERGE_TICKETS_SUCCESS,
          data: {
            ticket,
            mergedTickets,
          },
          optimist: optimisticData ? { type: REVERT, id: transactionId } : null,
        })

        return dispatch(doClearNonNamedTicketSearchResults())
      })
      .catch(() => {
        dispatch({
          type: types.MERGE_TICKETS_FAIL,
          optimist: optimisticData ? { type: REVERT, id: transactionId } : null,
        })
      })
      .finally(() => {
        dispatch({
          type: types.MERGE_TICKETS_COMPLETE,
          meta: {
            requestId: transactionId,
          },
        })
      })
  }
}
